import type { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { WorkspaceSwitcher } from 'components/WorkspaceSwitcher/WorkspaceSwitcher';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { DeviceSize, useDeviceSize } from 'wrappers/DeviceSizeProvider';
import { useWorkspace } from 'wrappers/WorkspaceProvider';

import { Button } from '@fragment/ui/src/components/Button/BaseButton/Button';
import { TertiaryButton } from '@fragment/ui/src/components/Button/TertiaryButton/TertiaryButton';
import { Icon } from '@fragment/ui/src/components/Icon';
import { LoadingIndicator } from '@fragment/ui/src/components/LoadingText';

import { ToastProvider } from '../../wrappers/ToastProvider';
import { MainNav } from '../SideNav/MainNav';
import { PaymentsNav } from '../SideNav/PaymentsNav';

type DashboardLayoutProps = {
  footer?: React.ReactNode;
  showLoadingIndicator?: boolean;
  collapseLeftNav?: boolean;
};

export const DashboardLayout = ({
  children,
  footer,
  showLoadingIndicator,
  collapseLeftNav = false,
}: PropsWithChildren<DashboardLayoutProps>) => {
  const deviceType = useDeviceSize();
  const [helpSubMenuOpen, setHelpSubMenuOpen] = useState<boolean>(false);
  const [isLeftNavExpanded, setLeftNavExpanded] = useState<boolean>(
    !collapseLeftNav
  );
  const [showCollapseButton, setShowCollapseButton] = useState<boolean>(false);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id;
  if (!workspaceId) {
    throw new Error('workspaceId not found');
  }
  const leftNavBackdropRef = useRef<HTMLDivElement>(null);
  const helpContentRef = useRef<HTMLDivElement>(null);
  const leftNavRef = useRef<HTMLDivElement>(null);

  const closeHelpMenus = useCallback(() => {
    setHelpSubMenuOpen(false);
  }, [setHelpSubMenuOpen]);

  const closeHelpMenuOnClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        helpSubMenuOpen &&
        helpContentRef.current &&
        !helpContentRef.current.contains(event.target as Node)
      ) {
        closeHelpMenus();
      }
    },
    [closeHelpMenus, helpSubMenuOpen]
  );

  const isMobileMenu = useMemo(
    () =>
      deviceType === DeviceSize.Phone ||
      deviceType === DeviceSize.Tablet ||
      deviceType === DeviceSize.WideTablet,
    [deviceType]
  );

  const closeLeftNavOnClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        leftNavBackdropRef.current &&
        event.target === leftNavBackdropRef.current &&
        isMobileMenu
      ) {
        setLeftNavExpanded(false);
      }
    },
    [setLeftNavExpanded, isMobileMenu]
  );

  useEffect(() => {
    document.addEventListener('mousedown', closeHelpMenuOnClickOutside);
    document.addEventListener('mousedown', closeLeftNavOnClickOutside);
    return () => {
      document.removeEventListener('mousedown', closeHelpMenuOnClickOutside);
      document.removeEventListener('mousedown', closeLeftNavOnClickOutside);
    };
  }, [closeHelpMenuOnClickOutside, closeLeftNavOnClickOutside]);

  useHotkeys('esc', closeHelpMenus, {
    enabled: true,
  });
  useHotkeys('mod+/', () => {
    // If nav is expanded and the help menu is open, close both.
    if (isLeftNavExpanded && helpSubMenuOpen) {
      closeHelpMenus();
    }
    setLeftNavExpanded(!isLeftNavExpanded);
  });

  const twTransition = 'transition-all duration-100 menu-transition';

  return (
    <ToastProvider>
      <div
        className={clsx([
          'h-screen text-md flex flex-col overflow-x-hidden relative ',
        ])}
        data-testid="dashboard-layout"
      >
        {/* width of the left nav is f21. to accomplish the animation, 
        we slide the nav in from off screen to the left. We expand the 
        nav bar to f25 when hidden so that the collapse / expand button 
        remains on screen */}
        {isMobileMenu && (
          <div
            className={clsx([
              'fixed top-0 left-0 w-screen h-screen bg-main-500  ',
              twTransition,
              isLeftNavExpanded ? 'opacity-40 z-20' : 'opacity-0 -z-10',
            ])}
            ref={leftNavBackdropRef}
          />
        )}
        <div className={clsx(['flex flex-col h-full'])}>
          <div
            className={clsx([
              isLeftNavExpanded ? 'px-f1 laptop:px-f2' : 'pr-f1 laptop:pr-f2',
              'h-full overflow-auto flex flex-row',
              twTransition,
            ])}
          >
            <header
              data-testid="main-nav-header"
              className={clsx([
                isLeftNavExpanded
                  ? 'left-0 w-f21 max-w-f21 justify-start'
                  : '-left-f22 laptop:-left-f21 w-f25 max-w-f25 justify-end',
                'absolute z-30 top-0 min-h-f4 max-h-f4 flex flex-row items-center pt-f0 laptop:pt-f2 min-w-f21 ',
                twTransition,
              ])}
              onMouseEnter={() => setShowCollapseButton(true)}
            >
              <div
                className={clsx([
                  'flex flex-row items-center justify-between w-full pl-f2',
                  workspace.type === 'payments' && 'pt-f2',
                ])}
              >
                <div
                  className={clsx(
                    workspace.type === 'payments' && [
                      'h-f8 border-b-[0.5px] border-main-300 flex items-center',
                    ]
                  )}
                >
                  <WorkspaceSwitcher disabled={!isLeftNavExpanded} />
                </div>
                <TertiaryButton
                  className={clsx(
                    'h-f2 w-f2 min-w-f2 min-h-f2',
                    isMobileMenu && 'opacity-100',
                    !isLeftNavExpanded || showCollapseButton
                      ? 'opacity-100'
                      : 'opacity-0',
                    twTransition
                  )}
                  onClick={() => setLeftNavExpanded(!isLeftNavExpanded)}
                  data-testid="main-nav-collapse-button"
                >
                  <span
                    className={clsx(
                      'bg-canvas hover:bg-negative hover:text-main flex items-center',
                      'justify-center border-main-300 border-[0.5px] w-f2 h-f2 text-main-500 '
                    )}
                  >
                    {isLeftNavExpanded ? '«' : `☰`}
                  </span>
                </TertiaryButton>
              </div>
            </header>
            <div
              ref={leftNavRef}
              onMouseEnter={() => setShowCollapseButton(true)}
              onMouseLeave={() => setShowCollapseButton(false)}
              className={clsx([
                'fixed laptop:absolute',
                isLeftNavExpanded ? ' left-f0' : '-left-f23',
                'flex flex-col w-f23 pl-f2 min-w-f23 max-w-f23 overflow-y-auto scrollbar-hide',
                'border-r-[0.5px] border-main-300',
                'pr-f2 pt-f6 laptop:pt-f8 h-full z-20 bg-canvas',
                twTransition,
              ])}
            >
              <nav
                data-testid={
                  isLeftNavExpanded ? 'left-nav-open' : 'left-nav-closed'
                }
                className={clsx(workspace.type === 'payments' && 'pt-f2')}
              >
                {workspace.type === 'payments' ? (
                  <PaymentsNav workspaceId={workspace.id} />
                ) : (
                  <MainNav workspaceId={workspace.id} />
                )}
              </nav>
            </div>
            <main
              data-testid="layout-main"
              className={clsx([
                'w-full h-full scrollbar-hide',
                isLeftNavExpanded && 'laptop:grow laptop:pl-f21',
                twTransition,
              ])}
            >
              {children}
            </main>
          </div>
          {footer && (
            <footer data-testid="footer" className="mt-auto sticky bottom-0">
              {footer}
            </footer>
          )}
        </div>
        {isLeftNavExpanded && (
          <div
            ref={helpContentRef}
            className={clsx([
              'absolute bottom-f2 z-20 left-f2 bg-opacity-100 flex flex-col justify-end',
              helpSubMenuOpen && 'z-20 h-1/4 w-1/3 min-w-f28 max-w-f45',
            ])}
            data-testid="help-container"
            onMouseEnter={() => setShowCollapseButton(true)}
          >
            {helpSubMenuOpen && (
              <div className="flex flex-col divide-y-[0.5px] divide-main-300 w-f14 shadow-nice border-[0.5px] border-main-300">
                <ExternalLink
                  href="https://fragment.dev/docs"
                  label="Documentation"
                />
                <ExternalLink
                  href="mailto:help@fragment.dev"
                  label="Contact"
                  showExternalIcon={false}
                />
              </div>
            )}
            <Button
              onClick={() => {
                if (helpSubMenuOpen) {
                  setHelpSubMenuOpen(false);
                } else {
                  setHelpSubMenuOpen(true);
                }
              }}
              background={
                helpSubMenuOpen ? 'bg-negative' : 'bg-main hover:bg-white'
              }
              color={
                helpSubMenuOpen ? 'text-main' : 'text-negative hover:text-main'
              }
              w="w-f2"
              h="h-f2"
              minH="min-h-f2"
              flexContainer="items-center"
            >
              {helpSubMenuOpen ? <Icon type="close" size="md" /> : '?'}
            </Button>
          </div>
        )}
        {showLoadingIndicator && (
          <div
            className={clsx([
              'absolute bottom-f2 z-50 right-f2 bg-opacity-100 flex flex-col justify-end',
              helpSubMenuOpen && 'z-50',
            ])}
          >
            <LoadingIndicator />
          </div>
        )}
      </div>
    </ToastProvider>
  );
};
